import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/Spinner';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipes } from '@/contexts/RecipesContext';
import { User, Calendar, ChefHat, Trophy, Medal, Compass, Link } from 'lucide-react';
import { Recipe, UserProfile as UserProfileType } from '@/types';
import RecipeCard from '@/components/RecipeCard';
import UserObjectives from '@/components/UserObjectives';
import UserLevel from '@/components/UserLevel';
import UserAchievements from '@/components/UserAchievements';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { generateReferralLink } from '@/utils/rewardfulUtils';

const UserProfile = () => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favoriteRecipes, setFavoriteRecipes] = useState<Recipe[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recipes } = useRecipes();
  const hasFetchedRef = useRef(false);
  const [pointsRefreshKey, setPointsRefreshKey] = useState(0);
  
  useEffect(() => {
    console.log("UserProfile mounting for username:", username);
    if (!hasFetchedRef.current) {
      fetchUserProfile();
      hasFetchedRef.current = true;
    }
    
    return () => {
      hasFetchedRef.current = false;
    };
  }, [username]);
  
  useEffect(() => {
    if (profile?.id && recipes.length > 0) {
      fetchUserFavoriteRecipes(profile.id);
    }
  }, [profile, recipes]);
  
  useEffect(() => {
    const handlePointsUpdated = (event: CustomEvent) => {
      if (profile && event.detail.userId === profile.id) {
        console.log("[UserProfile] Points updated event received:", event.detail);
        setProfile(prev => {
          if (!prev) return null;
          return {
            ...prev,
            points: event.detail.newPoints
          };
        });
        
        // Force refresh the UserLevel component
        setPointsRefreshKey(prev => prev + 1);
      }
    };
    
    const handleChallengeCompleted = (event: CustomEvent) => {
      if (profile && event.detail.userId === profile.id) {
        console.log("[UserProfile] Challenge completed event received:", event.detail);
        // Force a profile refresh to get updated points
        fetchUserProfile();
      }
    };
    
    window.addEventListener('points-updated', handlePointsUpdated as EventListener);
    window.addEventListener('challenge-completed', handleChallengeCompleted as EventListener);
    
    return () => {
      window.removeEventListener('points-updated', handlePointsUpdated as EventListener);
      window.removeEventListener('challenge-completed', handleChallengeCompleted as EventListener);
    };
  }, [profile]);
  
  const fetchUserProfile = async () => {
    if (!username) {
      setError('Usuario no especificado');
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`Fetching profile for username: ${username}`);
      
      // First try fetching by username
      let { data, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, username, avatar_url, description, created_at, objectives, favorites, points')
        .eq('username', username)
        .single();
        
      if (profileError) {
        console.log('Error al buscar por username, intentando por email:', profileError);
        
        // If username fails, try fetching by email
        const { data: emailData, error: emailError } = await supabase
          .from('profiles')
          .select('id, name, username, avatar_url, description, created_at, objectives, favorites, points')
          .eq('email', username)
          .single();
          
        if (emailError) {
          console.error('Error al buscar por email:', emailError);
          console.error('No se pudo encontrar el perfil con username o email:', username);
          setError('No se pudo encontrar el perfil de usuario');
          setIsLoading(false);
          return;
        }
        
        data = emailData;
      }
      
      console.log('Profile loaded successfully:', data);
      // Assign default points value of 0 if not available
      setProfile({
        ...data,
        points: data.points || 0 // Ensure points has a default value
      });
      
      // Save the points in localStorage so they're available to the UserLevel component
      if (data.id && data.points !== undefined) {
        localStorage.setItem(`user_points_${data.id}`, data.points.toString());
      }
    } catch (err) {
      console.error('Error inesperado:', err);
      setError('Error al cargar el perfil de usuario');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchUserFavoriteRecipes = async (userId: string) => {
    try {
      setLoadingRecipes(true);
      
      // Get user's favorites directly from the profile
      if (profile && profile.favorites && Array.isArray(profile.favorites) && profile.favorites.length > 0) {
        const favoriteIds = profile.favorites;
        
        if (favoriteIds.length > 0 && recipes.length > 0) {
          // Filter recipes based on favorite IDs
          const favorites = recipes.filter(recipe => 
            favoriteIds.includes(recipe.id)
          );
          setFavoriteRecipes(favorites);
        }
      } else {
        console.log("No favorites found in profile or invalid favorites data");
      }
    } catch (error) {
      console.error('Error fetching favorite recipes:', error);
    } finally {
      setLoadingRecipes(false);
    }
  };
  
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'No disponible';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return 'No disponible';
    }
  };
  
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map((part) => part[0]).join('').toUpperCase();
  };
  
  const getReferralShareMessage = () => {
    return '¡Únete a Guía Vegana con mi enlace de referido y obtén un 50% de descuento en tu primer mes!';
  };
  
  const handleCopyReferralLink = async () => {
    if (!profile?.id) return;
    
    try {
      const link = generateReferralLink(profile.id);
      await navigator.clipboard.writeText(link);
      toast.success('Enlace de referido copiado al portapapeles');
    } catch (error) {
      console.error('Error copying referral link:', error);
      toast.error('No se pudo copiar el enlace');
    }
  };
  
  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }
  
  if (error || !profile) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <User className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <h2 className="text-xl font-semibold mb-2">Usuario no encontrado</h2>
                <p className="text-gray-600 mb-4">{error || 'No se pudo encontrar el perfil solicitado'}</p>
                <Button 
                  variant="default" 
                  onClick={() => navigate('/')}
                >
                  Volver al inicio
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-4xl mx-auto">
          <CardHeader className="pb-0">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Avatar className="h-24 w-24 md:h-32 md:w-32 border-2 border-white shadow-md">
                <AvatarImage src={profile?.avatar_url} alt={profile?.name} />
                <AvatarFallback className="bg-vegana-primary text-white text-xl">
                  {getInitials(profile?.name || '')}
                </AvatarFallback>
              </Avatar>
              
              <div className="text-center md:text-left">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-2xl font-bold">{profile?.name}</CardTitle>
                  <UserLevel points={profile?.points || 0} refreshKey={pointsRefreshKey} compact />
                </div>
                <p className="text-gray-500 text-sm mt-1">@{profile?.username}</p>
                
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>Se unió el {formatDate(profile?.created_at)}</span>
                  </div>
                </div>
                
                {/* Display compact achievements */}
                {profile?.id && (
                  <div className="mt-3">
                    <UserAchievements userId={profile.id} compact />
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6 space-y-6">
            {profile?.description ? (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">Sobre mí</h3>
                <p className="text-gray-700 whitespace-pre-line">{profile.description}</p>
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                Este usuario no ha añadido una descripción todavía.
              </div>
            )}
            
            {/* User level information */}
            <div className="mt-6">
              <UserLevel points={profile?.points || 0} refreshKey={pointsRefreshKey} />
            </div>
            
            {/* User objectives */}
            <UserObjectives objectives={profile?.objectives || []} />
            
            {/* User achievements - moved here to be below objectives */}
            {profile?.id && (
              <div className="mt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  <span>Logros</span>
                </h3>
                <UserAchievements userId={profile.id} />
              </div>
            )}
            
            {/* User's referral link - only for logged in users viewing other profiles */}
            {user && user.id !== profile.id && (
              <div className="mt-6 p-4 bg-vegana-primary/10 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Link className="h-5 w-5 text-vegana-primary" />
                  <h3 className="font-medium">Únete usando su enlace de referido</h3>
                </div>
                <p className="text-sm mb-3">
                  Regístrate usando su enlace de referido y obtendrás un 50% de descuento en tu primer mes.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyReferralLink}
                  className="flex items-center gap-1"
                >
                  Copiar enlace de referido
                </Button>
              </div>
            )}
            
            {/* User's favorite recipes */}
            <div className="mt-8">
              <div className="flex items-center mb-4 gap-2">
                <ChefHat className="h-5 w-5 text-vegana-primary" />
                <h3 className="text-xl font-medium">Recetas favoritas</h3>
              </div>
              
              {loadingRecipes ? (
                <div className="flex justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : favoriteRecipes.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favoriteRecipes.slice(0, 3).map((recipe) => (
                    <RecipeCard key={recipe.id} recipe={recipe} />
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                  Este usuario no ha añadido recetas favoritas todavía.
                </div>
              )}
              
              {favoriteRecipes.length > 3 && (
                <div className="text-center mt-4">
                  <Button 
                    variant="outline"
                    onClick={() => navigate(`/recetas?user=${profile?.username}`)}
                  >
                    Ver todas las recetas favoritas
                  </Button>
                </div>
              )}
            </div>
            
            {user?.id === profile?.id && (
              <div className="mt-6 flex justify-center md:justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/perfil')}
                >
                  Editar mi perfil
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default UserProfile;
